import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';

interface ZoneOption {
  id: string | number;
  humanreadable_name?: string;
}

interface ZoneDropdownProps {
  zones: ZoneOption[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}

/**
 * Reusable custom zone picker — trigger button + portal menu + search.
 * Shared between heatmap compare-mode (FE-114-A) and branch selector contexts (FE-108).
 */
export function ZoneDropdown({ zones, value, onChange, className }: ZoneDropdownProps) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [hlIndex, setHlIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedZone = zones.find((z) => String(z.id) === value);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
  }, [open]);

  const filtered = zones.filter((z) =>
    (z.humanreadable_name || '').toLowerCase().includes(query.trim().toLowerCase()),
  );

  useEffect(() => {
    setHlIndex(i => (filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1)));
  }, [filtered.length]);

  useEffect(() => { setHlIndex(0); }, [query, open]);

  useEffect(() => {
    if (!open) return;
    itemRefs.current[hlIndex]?.scrollIntoView({ block: 'nearest' });
  }, [hlIndex, open]);

  const pickAt = (idx: number) => {
    const z = filtered[idx];
    if (!z) return;
    onChange(String(z.id));
    setOpen(false);
    setQuery('');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHlIndex(i => (filtered.length === 0 ? 0 : Math.min(i + 1, filtered.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHlIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered.length > 0) {
      e.preventDefault();
      pickAt(Math.min(hlIndex, filtered.length - 1));
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex h-9 min-w-0 items-center gap-2 rounded-lg border border-border bg-background px-3 text-sm transition-colors',
          'hover:border-primary/40 hover:bg-muted',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          open && 'border-primary/50 ring-2 ring-ring/30',
          className,
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate font-medium">
          {selectedZone?.humanreadable_name ?? value}
        </span>
        <ChevronDown className="size-4 text-muted-foreground shrink-0 ml-auto" />
      </button>

      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width }}
          className="z-[100] flex max-h-72 flex-col overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg animate-in fade-in zoom-in-95 duration-100"
        >
          <div className="border-b border-border p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={t('branchSelector.search')}
                className="h-8 w-full rounded-md border border-input bg-background pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <div className="py-3 text-center text-xs text-muted-foreground">
                {t('branchSelector.notFound')}
              </div>
            )}
            {filtered.map((z, idx) => {
              const id = String(z.id);
              const isActive = id === value;
              const isHighlighted = idx === hlIndex;
              return (
                <button
                  key={id}
                  ref={el => { itemRefs.current[idx] = el; }}
                  type="button"
                  onClick={() => pickAt(idx)}
                  onMouseEnter={() => setHlIndex(idx)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    isActive
                      ? 'bg-primary-muted text-primary-muted-foreground'
                      : isHighlighted
                        ? 'bg-muted text-foreground'
                        : 'hover:bg-muted hover:text-foreground',
                  )}
                >
                  <span className="flex-1 truncate">{z.humanreadable_name}</span>
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
